import { plural } from '../components/kits/format'

/**
 * Rynek zleceń „Ogłoś na Rynku” (decyzje właściciela 2026-10-21, repo VV `zadania/2026-10_rynek-zlecen.md`).
 *
 * Kowalski z kontem ogłasza zlecenie (z zestawu, z raportu „Sprawdź ofertę” albo wprost), ogłoszenie trafia do
 * zbiornika, a właściciel ręcznie pokazuje je firmom. Backend: aplikacja `rynek`, `/api/v1/rynek/`.
 *
 * Tryb kontaktu (2026-10-22, `zadania/2026-10_rynek-dopasowanie-i-firmy.md` w VV): „dopasowanie” (najwyżej 3 firmy
 * z okolicy dostają kontakt od razu, klient widzi które i może poprosić o koniec kontaktu) albo „najpierw oferty”
 * (firmy widzą ogłoszenie bez kontaktu, kontakt dostaje firma wybrana przez klienta). Wybór obowiązkowy, nic nie
 * jest zaznaczone z góry (RODO).
 *
 * `RYNEK_WLACZONY = false` chowa wejścia na Rynek i przywraca „Poproś o kontrofertę” pod raportem.
 */
export const RYNEK_WLACZONY = true

// Treści zgody zapisywane przy ogłoszeniu (backend `Ogloszenie.tresc_zgody`, rozliczalność RODO). Zmiana treści
// wymaga zgody właściciela i zgodności z polityką prywatności (rozdział 10h) i regulaminem (§ 4c).
export const TRESC_ZGODY = {
  dopasowanie: 'Zgadzam się, żeby VoltValue przekazało moje imię, e-mail i telefon najwyżej 3 firmom fotowoltaicznym '
    + 'z mojej okolicy (moje województwo albo sąsiednie), zweryfikowanym w rejestrze firm i dobranym do tego '
    + 'ogłoszenia, w celu przygotowania oferty. Nazwy tych firm zobaczę w „Moich ogłoszeniach”. Zgodę mogę wycofać '
    + 'w każdej chwili.',
  najpierw_oferty: 'Zgadzam się, żeby VoltValue pokazało moje ogłoszenie firmom fotowoltaicznym z mojej okolicy '
    + '(bez imienia, e-maila i telefonu) i przekazało moje imię, e-mail i telefon firmie, którą wybiorę.',
  // Mini przetarg (2026-10-24): projekt do akceptacji właściciela (repo VV, dokument logiki przetargu, punkt 7).
  przetarg: 'Zgadzam się, żeby VoltValue pokazało moje zlecenie (bez imienia, e-maila i telefonu) firmom '
    + 'fotowoltaicznym dobranym do niego, a moje imię, e-mail i telefon przekazało tylko tym firmom, które wybiorę '
    + 'spośród tych, które zgłoszą się do zlecenia. Zgodę mogę wycofać w każdej chwili.',
  // Wariant D z adresem (makieta 12, D10; okno adresu po „Wystaw zlecenie”). Projekt do akceptacji właściciela
  // i pakietu tekstów prawnych P10 (regulamin Rynku, polityka prywatności).
  zlecenie: 'Zgadzam się, żeby VoltValue pokazało moje zlecenie, bez imienia, e-maila, telefonu i dokładnego adresu, '
    + 'wykonawcom z mojej okolicy dobranym do tego zlecenia, a moje dane przekazało wyłącznie wykonawcom, których '
    + 'wybiorę. Zgodę mogę wycofać w każdej chwili.',
}

export const MAKS_DOPASOWANYCH = 3

/**
 * Mini przetarg (decyzje właściciela 2026-10-24, repo VV `zadania/2026-10_rynek-przetarg-logika.md`): klient wybiera,
 * ile firm ma się starać o zlecenie; zaproszone firmy w 48 h potwierdzają udział albo odmawiają; klient widzi te,
 * które potwierdziły (bez rankingu), i zaznacza, które dostaną kontakt. Nowe zlecenia mają tylko ten tryb.
 */
export const TRYB_PRZETARGU = 'przetarg'
export const LICZBY_FIRM = [3, 6, 9]
export const DOMYSLNA_LICZBA_FIRM = 3
export const GODZIN_NA_POTWIERDZENIE = 48
// Uprawnienie „Oferty na Rynku” (2026-10-24, backend `rynek/api.py` `BEZ_UPRAWNIENIA`): zlecenia obsługuje konto
// główne i osoby, którym je nadało; reszta zespołu widzi ten komunikat zamiast zleceń.
export const BEZ_UPRAWNIENIA_RYNKU = 'Zlecenia z Rynku obsługują w Twojej firmie osoby z uprawnieniem „Oferty na Rynku”. '
  + 'Poproś konto główne firmy o jego nadanie.'
/** „1 firmę”, „3 firmy”, „6 firm” (biernik: zaprosimy N …); odmiana jak w całym froncie (`kits/format.plural`). */
export const firmy = (n) => `${n} ${plural(n, 'firmę', 'firmy', 'firm')}`

// Powody odmowy udziału (backend `PowodOdmowy`).
export const POWODY_ODMOWY = [
  { klucz: 'termin', nazwa: 'Termin mi nie pasuje' },
  { klucz: 'kwota', nazwa: 'Budżet jest za niski' },
  { klucz: 'moc', nazwa: 'Za mała albo za duża instalacja' },
  { klucz: 'lokalizacja', nazwa: 'Za daleko' },
  { klucz: 'sprzet', nazwa: 'Nie montuję takiego sprzętu' },
  { klucz: 'inne', nazwa: 'Inny powód' },
]

// Skąd sprzęt (decyzja 2026-10-23, backend `Ogloszenie.sprzet`): dwie drogi i „Pokaż różnice” (= obie drogi).
// Nazwy jak na stronie dla firm (decyzje 2026-10-24): „Instalacja ze sprzętem firmy” zawsze pierwsza, potem montaż
// sprzętu klienta; bez „ekipy” i „firmy ze swoim sprzętem”. Bez żargonu „pod klucz” i bez form męskich („sam”).
export const SPRZET = [
  { klucz: 'z_firma', nazwa: 'Instalacja ze sprzętem firmy',
    opis: 'Jedna firma dostarcza sprzęt, montuje i załatwia formalności. Zwykle drożej, ale masz jedną gwarancję i jedną osobę do kontaktu.' },
  { klucz: 'kupie_sam', nazwa: 'Montaż mojego sprzętu',
    opis: 'Zestaw z asystenta kupujesz po cenach sklepów, przyjeżdża do Ciebie, a firma go montuje. Zwykle taniej.' },
  { klucz: 'obie', nazwa: 'Pokaż różnice',
    opis: 'Porównaj obie drogi. Jeśli zostaniesz przy tej opcji, dobierzemy firmy obu rodzajów.' },
]

/**
 * Kto zajmie się sprzętem (makieta 12, D12; decyzja D-g: dwa pola zlecenia `kto_dobiera` i `kto_kupuje` zamiast
 * `sprzet`, para „wykonawca dobiera, klient kupuje” zabroniona). Kolejność jak na stronie „VoltValue dla domu”.
 * `sprzet`: wartość dawnego pola dla backendu sprzed pakietu P0 (do usunięcia po scaleniu P0).
 */
export const SPRZET_ZLECENIA = [
  { klucz: 'wykonawca', nazwa: 'Wszystko po stronie wykonawcy', krotko: 'Jeden wykonawca',
    opis: 'Wykonawca dobiera sprzęt, kupuje go, montuje i załatwia formalności. Zwykle drożej, ale masz jedną gwarancję i jedną osobę do kontaktu.',
    kto_dobiera: 'wykonawca', kto_kupuje: 'wykonawca', sprzet: 'z_firma' },
  { klucz: 'zestaw_wykonawca', nazwa: 'Dobieramy zestaw, wykonawca go kupuje', krotko: 'Zestaw od nas',
    opis: 'Asystent dobiera zgodny zestaw, a wykonawcy wyceniają ten sam zestaw, kupują go i montują. Ich oferty łatwo porównać.',
    kto_dobiera: 'voltvalue', kto_kupuje: 'wykonawca', sprzet: 'z_firma' },
  { klucz: 'zestaw_klient', nazwa: 'Dobieramy zestaw, zamawiasz samodzielnie', krotko: 'Sprzęt ze sklepu',
    opis: 'Zestaw zamawiasz w sklepie z najniższą ceną, przyjeżdża do Ciebie, a wykonawca go montuje. Zwykle najtaniej.',
    kto_dobiera: 'voltvalue', kto_kupuje: 'klient', sprzet: 'kupie_sam' },
]

// Konkretny sprzęt klienta (Projekt 3D, zestaw, oferta albo „Wiem, jaki sprzęt chcę”, uwagi przeglądu F11, decyzja
// właściciela 2026-10-25): pytamy tylko, kto go kupuje. Ta sama para pól co „Dobieramy zestaw” (`kto_dobiera=voltvalue`),
// więc wykonawca wycenia ten sprzęt, a inny model proponuje tylko z podanym powodem (backend `przetarg.pozycje_oferty`).
export const SPRZET_KONKRETNY = [
  { klucz: 'konkretny_wykonawca', nazwa: 'Wykonawca kupuje ten sprzęt', krotko: 'Sprzęt od wykonawcy',
    opis: 'Wykonawcy wyceniają ten sprzęt, kupują go i montują. Inny model mogą zaproponować tylko z podanym powodem.',
    kto_dobiera: 'voltvalue', kto_kupuje: 'wykonawca', sprzet: 'z_firma' },
  { klucz: 'konkretny_klient', nazwa: 'Kupię go samodzielnie', krotko: 'Sprzęt ze sklepu',
    opis: 'Zamawiasz ten sprzęt w sklepie, przyjeżdża do Ciebie, a wykonawca go montuje i załatwia formalności. Zwykle najtaniej.',
    kto_dobiera: 'voltvalue', kto_kupuje: 'klient', sprzet: 'kupie_sam' },
]

// Porównanie trzech opcji w „Pokaż różnice” (makieta 12): [temat, wszystko po stronie wykonawcy, zestaw kupuje
// wykonawca, zestaw zamawiasz samodzielnie].
export const ROZNICE_SPRZETU_ZLECENIA = [
  ['Kto dobiera', 'Wykonawca, według swojej oferty.', 'Asystent VoltValue, według Twoich celów.', 'Asystent VoltValue, według Twoich celów.'],
  ['Kto kupuje', 'Wykonawca.', 'Wykonawca, według listy z asystenta.', 'Ty, w sklepie z najniższą ceną.'],
  ['Cena', 'Zwykle najwyższa: wykonawca dolicza sprzęt ze swoją marżą.', 'Sprzęt po cenach wykonawców, ale wszyscy wyceniają ten sam zestaw, więc łatwo porównasz.', 'Zwykle najniższa: sprzęt po cenach sklepów, które porównujemy.'],
  ['Gwarancja', 'Jeden wykonawca odpowiada za całość.', 'Jeden wykonawca odpowiada za całość.', 'Na sprzęt od producenta, na montaż od wykonawcy.'],
  ['Dla kogo', 'Gdy wolisz wygodę i jedną osobę do kontaktu.', 'Gdy chcesz dobry sprzęt bez zamawiania.', 'Gdy chcesz zapłacić jak najmniej.'],
]

/** Opcja sprzętu z wejścia: nowe pola, klucz opcji albo dawne `sprzet` z asystenta (`kupie_sam`, `z_firma`). */
export const sprzetZWejscia = (p) => {
  if (!p) return ''
  const wg = SPRZET_ZLECENIA.find((o) => o.klucz === p.sprzet
    || (p.kto_dobiera && o.kto_dobiera === p.kto_dobiera && o.kto_kupuje === p.kto_kupuje))
  if (wg) return wg.klucz
  if (p.sprzet === 'kupie_sam') return 'zestaw_klient'
  if (p.sprzet === 'z_firma') return 'wykonawca'
  return ''
}

/** Ofert do porównania: 3, 6 albo 9; nic nie jest zaznaczone z góry (makieta 12). */
export const LICZBY_OFERT = [
  { n: 3, opis: 'Krótka lista' },
  { n: 6, opis: 'Do porównania' },
  { n: 9, opis: 'Szeroki wybór' },
]

// Porównanie dróg w „Pokaż różnice”: [temat, instalacja ze sprzętem firmy, montaż mojego sprzętu].
export const ROZNICE_SPRZETU = [
  ['Cena', 'Zwykle wyższa: firma dolicza sprzęt ze swoją marżą.', 'Zwykle niższa: sprzęt po cenach sklepów, które pokazuje asystent doboru.'],
  ['Kto co robi', 'Jedna firma dostarcza sprzęt, montuje i załatwia formalności.', 'Sprzęt przyjeżdża do Ciebie, a firmę umawiasz na montaż pod ten sprzęt.'],
  ['Gwarancja', 'Jedna firma odpowiada za całość.', 'Na sprzęt od producenta, na montaż od firmy, która montuje.'],
  ['Dla kogo', 'Gdy wolisz wygodę i jedną osobę do kontaktu.', 'Gdy chcesz porównać ceny i zaoszczędzić.'],
]

// Zakres usług firmy (backend `Company.rynek_zakres`): nazwy w ankiecie firmy i znaczniki przy odpowiedzi dla klienta.
export const ZAKRESY_FIRMY = [
  { klucz: 'ze_sprzetem', ankieta: 'Instalacja ze sprzętem firmy', znacznik: 'Instalacja ze sprzętem firmy' },
  { klucz: 'montaz', ankieta: 'Montaż na sprzęcie klienta', znacznik: 'Montaż Twojego sprzętu' },
  { klucz: 'serwis', ankieta: 'Serwis i rozbudowa instalacji', znacznik: 'Serwis' },
]

// Zamknięte listy ankiety firmy (decyzja właściciela 2026-11-01), ta sama lista w backendzie: `rynek/specjalizacje.py`.
// Specjalizacje idą na kartę „Specjalizuje się w”, zasoby tylko do profilu i dopasowania.
export const SPECJALIZACJE_FIRMY = [
  { grupa: 'Instalacje', pozycje: ['Magazyny energii', 'Magazyn do istniejącej instalacji', 'Rozbudowa instalacji', 'Zasilanie awaryjne', 'Off-grid', 'Instalacje dla firm i rolników', 'Garaże i wiaty'] },
  { grupa: 'Usługi', pozycje: ['Montaż ładowarek EV', 'Pompy ciepła', 'Serwis instalacji po innej firmie', 'Modernizacja rozdzielnicy', 'Optymalizatory', 'Przeglądy po montażu', 'Nowe domy'] },
]
export const ZASOBY_FIRMY = ['Własna ekipa', 'Załatwiamy formalności (operator, dotacje)', 'Przekopy', 'Własna koparka', 'Własna zwyżka', 'Rusztowania']

export const TYPY_OGLOSZENIA = [
  { klucz: 'pv', nazwa: 'Fotowoltaika' },
  { klucz: 'pv_magazyn', nazwa: 'Fotowoltaika z magazynem' },
  { klucz: 'magazyn', nazwa: 'Magazyn energii' },
  { klucz: 'rozbudowa', nazwa: 'Rozbudowa instalacji' },
  { klucz: 'inne', nazwa: 'Inne' },
]

export const TERMINY = [
  { klucz: 'szybko', nazwa: 'Jak najszybciej' },
  { klucz: 'do_1_mies', nazwa: 'W ciągu miesiąca' },
  { klucz: 'pozniej', nazwa: 'Później' },
]

// Stan budynku w zleceniu (`opis.budynek`, 2026-11-02); dwa ostatnie punktują firmy z usługą „Nowe domy” (backend `BUDYNKI_NOWE`).
export const BUDYNKI_RYNKU = ['Istniejący dom', 'W trakcie budowy', 'Planowana budowa']
export const DACHY_RYNKU = ['Dachówka', 'Blacha', 'Papa', 'Grunt', 'Inny']

// Status ogłoszenia słowami Kowalskiego (backend: nowe, udostepnione, wybrano, zamkniete, odrzucone).
export const STATUS_DLA_KOWALSKIEGO = {
  // Zlecenie gościa przed kliknięciem linku z maila (decyzja właściciela 2026-10-26): nikt go jeszcze nie widzi.
  czeka_na_email: { nazwa: 'Czeka na potwierdzenie e-maila', kolor: 'bg-amber-400' },
  nowe: { nazwa: 'Sprawdzamy zlecenie', kolor: 'bg-amber-400' },
  udostepnione: { nazwa: 'Wykonawcy się zgłaszają', kolor: 'bg-blue-600' },
  wybrano: { nazwa: 'Wykonawcy wybrani', kolor: 'bg-emerald-600' },
  zamkniete: { nazwa: 'Zamknięte', kolor: 'bg-gray-400' },
  odrzucone: { nazwa: 'Nieprzyjęte', kolor: 'bg-gray-400' },
}

// Komentarz do zlecenia na końcu każdej drogi, do 500 znaków (decyzja właściciela 2026-10-29); backend przyjmuje do 2000.
export const MAKS_KOMENTARZA = 500
